import React from "react";
import { useContext, useEffect, useRef } from "react";
import { LangContext, iconSize } from "../App";
import { IntlProvider } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";

const Competences = () => {
  const language = useContext(LangContext);
  const techSkills_pl = [
    "Tworzenie nowoczesnych aplikacji webowych SPA z wykorzystaniem React.js.",
    "Projektowanie i implementacja responsywnych interfejsów użytkownika (Bootstrap, Material UI, CSS3).",
    "Tworzenie backendów i REST API w PHP.",
    "Projektowanie, optymalizacja i utrzymanie baz danych MySQL oraz MSSQL.",
    "Integracja systemów zewnętrznych i automatyzacja procesów biznesowych.",
    "Projektowanie architektury aplikacji webowych oraz przepływu danych.",
    "Wdrażanie i utrzymanie aplikacji na serwerach VPS oraz środowiskach Linux.",
    "Konfiguracja Docker oraz automatyzacja procesów wdrożeniowych.",
    "Konfiguracja domen, DNS, poczty elektronicznej oraz certyfikatów SSL.",
    "Bezpieczeństwo aplikacji, kopie zapasowe i ochrona danych.",
    "Optymalizacja wydajności aplikacji oraz SEO techniczne.",
    "Monitoring, utrzymanie i rozwój systemów produkcyjnych.",
  ];

  const techSkills_en = [
    "Development of modern Single Page Applications (SPA) using React.js.",
    "Design and implementation of responsive user interfaces with Bootstrap, Material UI, and CSS3.",
    "Backend development and REST API implementation in PHP.",
    "Design, optimization, and maintenance of MySQL and Microsoft SQL Server databases.",
    "Integration with third-party systems and business process automation.",
    "Web application architecture design and data flow planning.",
    "Deployment and maintenance of applications on VPS servers and Linux environments.",
    "Docker configuration and deployment process automation.",
    "Domain, DNS, email service, and SSL certificate configuration.",
    "Application security, data protection, and backup strategies.",
    "Application performance optimization and technical SEO.",
    "Monitoring, maintenance, and continuous improvement of production systems.",
  ];

  const managementSkills_pl = [
    "Kierowanie działem IT oraz koordynacja pracy zespołu programistów i administratorów.",
    "Planowanie i realizacja projektów informatycznych – od analizy potrzeb biznesowych po wdrożenie i rozwój systemów.",
    "Współpraca z zarządem oraz klientami w zakresie definiowania wymagań i planowania rozwoju produktów.",
    "Optymalizacja procesów biznesowych poprzez projektowanie i wdrażanie dedykowanych rozwiązań informatycznych.",
    "Zarządzanie budżetem IT oraz kontrola kosztów utrzymania infrastruktury i systemów.",
    "Nadzór nad bezpieczeństwem, ciągłością działania oraz rozwojem środowiska IT.",
    "Zarządzanie pełnym cyklem życia aplikacji: analiza, projektowanie, wdrożenie, utrzymanie i rozwój.",
    "Tworzenie oraz rozwój własnych produktów internetowych i aplikacji wykorzystywanych komercyjnie.",
  ];

  const managementSkills_en = [
    "Managing the IT department and coordinating the work of software developers and system administrators.",
    "Planning and delivering IT projects from business requirements analysis through implementation and ongoing system development.",
    "Collaborating with executive management and clients to define business requirements and product development strategies.",
    "Optimizing business processes by designing and implementing tailored IT solutions.",
    "Managing IT budgets and optimizing infrastructure and system maintenance costs.",
    "Ensuring IT security, business continuity, and the continuous development of the IT environment.",
    "Managing the full application lifecycle, including analysis, design, implementation, maintenance, and continuous improvement.",
    "Creating and developing commercial web applications and proprietary digital products.",
  ];
  const styles = {
    wrapper: {
      minHeight: "100vh",
      padding: "80px 24px 60px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    headerZone: {
      textAlign: "center",
      marginBottom: "48px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#4f46e5",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "8px",
    },
    mainTitle: {
      fontSize: "36px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
    },
    splitLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(450px, 100%), 1fr))",
      gap: "32px",
      alignItems: "start",
    },
    competenceCard: {
      background: "#ffffff",
      borderRadius: "24px",
      padding: "40px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.05)",
      boxSizing: "border-box",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "28px",
      borderBottom: "1px solid #f1f5f9",
      paddingBottom: "20px",
    },
    iconWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      backgroundColor: "#eef2ff",
      color: "#4f46e5",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#0f172a",
      margin: 0,
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      fontSize: "15px",
      color: "#475569",
      lineHeight: "1.5",
    },
    checkIcon: {
      flexShrink: 0,
      marginTop: "3px",
      color: "#10b981",
    },
  };

  return (
    <IntlProvider locale={language}>
      <AnimatePresence>
        <motion.div
          style={styles.container}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={textVariants}
        >
          <div style={styles.wrapper}>
            <div style={styles.container}>
              <>
                {language === "pl" && (
                  <div style={styles.headerZone}>
                    <h1 style={styles.mainTitle}>
                      Doświadczenie i specjalizacje
                    </h1>
                  </div>
                )}
                {language === "en" && (
                  <div style={styles.headerZone}>
                    <h1 style={styles.mainTitle}>Experience & Expertise</h1>
                  </div>
                )}
              </>
              <div style={styles.splitLayout}>
                <div style={styles.competenceCard}>
                  <div style={styles.cardHeader}>
                    <div style={styles.iconWrapper}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                      </svg>
                    </div>
                    {language === "pl" && <h2>Umiejętności techniczne</h2>}
                    {language === "en" && <h2>Technical skills</h2>}
                  </div>

                  {language === "pl" && (
                    <div style={styles.list}>
                      {techSkills_pl.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {language === "en" && (
                    <div style={styles.list}>
                      {techSkills_en.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={styles.competenceCard}>
                  <div style={styles.cardHeader}>
                    <div style={styles.iconWrapper}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                    {language === "pl" && <h2>Zarządzanie IT i projektami</h2>}
                    {language === "en" && (
                      <h2>IT Management & Project Delivery</h2>
                    )}
                  </div>
                  {language === "pl" && (
                    <div style={styles.list}>
                      {managementSkills_pl.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {language === "en" && (
                    <div style={styles.list}>
                      {managementSkills_en.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </IntlProvider>
  );
};

export default Competences;
